import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { queryKeys } from "@nasam/kernel";
import type { DevicesRepository } from "../domain";

/** The catalog is small and `GET /devices/types` has no search -- one page
 *  holds it, and the Combobox filters it locally. */
const CATALOG_LIMIT = 100;

export interface DeviceTypeOption {
  readonly value: string;
  readonly label: string;
}

export interface UseDeviceTypeOptionsResult {
  readonly options: readonly DeviceTypeOption[];
  readonly isLoading: boolean;
}

/** Device types from the shared catalog, for a `Combobox`. */
export function useDeviceTypeOptions(
  repository: DevicesRepository | undefined,
): UseDeviceTypeOptionsResult {
  const { i18n } = useTranslation();
  const isAr = i18n.language.startsWith("ar");
  const query = useQuery({
    queryKey: queryKeys.deviceTypes.list({ limit: CATALOG_LIMIT }),
    queryFn: () => repository!.listTypes({ page: 1, limit: CATALOG_LIMIT }),
    enabled: repository !== undefined && typeof repository.listTypes === "function",
  });

  const options = useMemo(
    () =>
      (query.data?.data ?? []).map((deviceType) => ({
        value: deviceType.id,
        label: isAr
          ? deviceType.nameAr || deviceType.nameEn
          : deviceType.nameEn || deviceType.nameAr,
      })),
    [query.data, isAr],
  );

  return { options, isLoading: query.isLoading };
}
